// Correção UX v2 §5.2 — Sessões (/sessions): réplica LITERAL do wireframe
// "03 Sessões" (docs/RAIOS-UX-SIDEBAR-SPEC.html), anatomia portada verbatim em
// src/styles/wf-sessions.css (.wf-*). Decisões do Breno sobrescrevem o wireframe:
//   - §5.2.1 card em 4 linhas (status dot · badges+msgs · ID+companion · start/end)
//   - §5.2.2 fix do espaço no rename (stopPropagation no input + card ignora filhos)
//   - §5.2.3 slash autocomplete compartilhado (<SlashAutocomplete/>) no composer
//   - §5.2.4 sidebar direita self-stretch na altura do chat; <xl vira disclosure
//   - §5.2.5 ações rápidas ENVIAM mensagem na sessão ativa (sem modais de criação)
//   - §5.2.6 runtime real (session.runtime top-level; heurística = fallback)
//   - §5.2.7 nova conversa honra modelo: 1ª mensagem leva { provider_key, model_key }
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  api,
  type Session,
  type SessionMessage,
  type CompanionListItem,
  type UploadFileResponse,
} from '../lib/api';
import {
  Bot,
  ChevronLeft,
  ClipboardList,
  Clock,
  Copy,
  Loader2,
  Lock,
  MessageCircle,
  MessageSquare,
  Mic,
  Paperclip,
  Plus,
  Radio,
  Search,
  Send,
  Workflow,
  Wrench,
  X,
  Zap,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/Toast';
import { PageHeader } from '@/components/layout/PageHeader';
import { MarkdownViewer } from '@/components/raios/MarkdownViewer';
import { SlashAutocomplete } from '@/components/chat/SlashAutocomplete';
import { useSupabaseRealtime } from '@/hooks/useSupabaseRealtime';
import { cn } from '@/lib/utils';
import { fmtDateTime } from '@/lib/format';
import { SessionCard } from '@/components/sessions/SessionCard';
import { EditableTitle } from '@/components/sessions/EditableTitle';
import {
  NewConversationModal,
  type NewConversationSelection,
} from '@/components/sessions/NewConversationModal';
import { ControlPlanePanel } from '@/components/llm/ControlPlanePanel';
import {
  deriveSessionRuntime,
  sessionDisplayTitle,
  sessionSummary,
  shortSessionId,
  copySessionId,
} from '@/components/sessions/session-utils';
import '@/styles/wf-sessions.css';

/* ─── Helpers ─────────────────────────────────────────────────────────────── */

const CHANNEL_LABELS: Record<string, string> = {
  dashboard: 'Dashboard',
  telegram: 'Telegram',
  cli: 'CLI',
  voice: 'Voice',
};

interface ChatMessage {
  role: 'user' | 'assistant' | 'error';
  content: string;
  timestamp: string;
}

function toChatMessages(messages: SessionMessage[] | undefined): ChatMessage[] {
  return (messages ?? []).map((m) => ({
    role: m.role,
    content: m.content,
    timestamp: m.created_at ?? new Date().toISOString(),
  }));
}

// §5.2.5 — Ações rápidas = MENSAGEM (decisão Breno): pré-preenchem e ENVIAM na
// sessão ativa. Mesma anatomia/estilo entre os 4 (.wf-qabtn do mockup).
const QUICK_ACTIONS: Array<{ key: string; label: string; message: string; icon: LucideIcon }> = [
  {
    key: 'demand',
    label: 'Passar nova demanda',
    message: 'Quero criar uma nova demanda — me guia.',
    icon: ClipboardList,
  },
  {
    key: 'agent',
    label: 'Criar novo agente',
    message: 'Quero criar um novo agente — me guia.',
    icon: Bot,
  },
  {
    key: 'skill',
    label: 'Criar nova skill',
    message: 'Quero criar uma nova skill — me guia.',
    icon: Wrench,
  },
  {
    key: 'pipeline',
    label: 'Criar novo pipeline',
    message: 'Quero criar um novo pipeline — me guia.',
    icon: Workflow,
  },
];

/* ─── Component ───────────────────────────────────────────────────────────── */

export function SessionsPage() {
  const { toast } = useToast();

  // Lista (esquerda)
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Sessão selecionada (centro/direita)
  const [selected, setSelected] = useState<Session | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);

  // Rascunho de conversa nova (§5.2.7): a sessão NÃO é pré-criada — a 1ª
  // mensagem vai sem session_id e leva o override de provedor/modelo (o
  // backend só honra o override na criação da sessão).
  const [draftOpen, setDraftOpen] = useState(false);
  const [pendingModelOverride, setPendingModelOverride] = useState<{
    provider_key?: string;
    model_key?: string;
  } | null>(null);

  // Composer
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [attachments, setAttachments] = useState<UploadFileResponse[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  // Companions (card "Companion da sessão" + envio + modal nova conversa).
  // 1 fetch cacheado — também resolve companion_id → nome nos cards (não N+1).
  const [companions, setCompanions] = useState<CompanionListItem[]>([]);
  const [companionsLoading, setCompanionsLoading] = useState(true);
  // Companion escolhido no modal — usado quando a sessão não traz companion_id.
  const [pendingCompanionId, setPendingCompanionId] = useState<string | null>(null);

  // Modais
  const [newConvOpen, setNewConvOpen] = useState(false);

  /* ─── Loaders ───────────────────────────────────────────────────────────── */

  const loadSessions = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await api.listSessions({ per_page: 50 });
      setSessions(Array.isArray(data) ? data : []);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err ?? 'Erro ao carregar sessões');
      if (
        msg.includes('non-whitespace') ||
        msg.includes('Unexpected') ||
        msg.includes('JSON') ||
        msg.includes('parse')
      ) {
        setError('Endpoint de sessões indisponível. Verifique se o backend suporta /v1/sessions.');
      } else {
        setError(msg.split('\n')[0]);
      }
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadSessions();
  }, [loadSessions]);

  // Realtime de companion_sessions (Breno 11/06): PATCH LOCAL a partir da row
  // do evento — ZERO refetch da listagem/sessão por evento. O que muda numa
  // sessão (título auto, resumo, ended_at, contagem) é aplicado direto no
  // estado; título/resumo da sidebar direita atualizam de graça. Sem fetch.
  useSupabaseRealtime({ table: 'companion_sessions' }, (payload) => {
    if (!payload) return;
    if (payload.eventType === 'DELETE') {
      const oldId = (payload.old as { id?: string } | null)?.id;
      if (oldId) setSessions((prev) => prev.filter((s) => s.id !== oldId));
      return;
    }
    const row = payload.new as Record<string, unknown> | null;
    if (!row || typeof row.id !== 'string') return;
    const metadata = (row.metadata ?? null) as Record<string, unknown> | null;
    const patch: Partial<Session> & { id: string } = {
      id: row.id,
      ...(row.channel !== undefined ? { channel: row.channel as Session['channel'] } : {}),
      ...(row.title !== undefined ? { title: (row.title as string | null) ?? null } : {}),
      ...(metadata
        ? {
            summary: typeof metadata.summary === 'string' ? metadata.summary : undefined,
            runtime: typeof metadata.runtime === 'string' ? metadata.runtime : undefined,
            metadata,
          }
        : {}),
      ...(row.ended_at !== undefined ? { ended_at: row.ended_at as string | null } : {}),
      ...(row.started_at !== undefined ? { started_at: row.started_at as string } : {}),
      ...(row.updated_at !== undefined ? { updated_at: row.updated_at as string } : {}),
      ...(row.companion_id !== undefined
        ? { companion_id: row.companion_id as string | null }
        : {}),
      ...(Array.isArray(row.messages) ? { message_count: row.messages.length } : {}),
    };
    setSessions((prev) => {
      const exists = prev.some((s) => s.id === patch.id);
      if (!exists && payload.eventType === 'INSERT') {
        return [patch as Session, ...prev];
      }
      return prev.map((s) => (s.id === patch.id ? { ...s, ...patch } : s));
    });
    // Sessão aberta: atualiza título/resumo/end SEM tocar nas mensagens do
    // chat (estado local do composer continua dono da conversa em andamento).
    setSelected((prev) => {
      if (!prev || prev.id !== patch.id) return prev;
      const rest: Partial<Session> = { ...patch };
      delete rest.message_count;
      return { ...prev, ...rest };
    });
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await api.listCompanions();
        if (!cancelled) setCompanions(list);
      } catch {
        // Best-effort — card do companion degrada pra "não identificado"
      } finally {
        if (!cancelled) setCompanionsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Scroll pro fim a cada mensagem nova
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  /* ─── Derivações ────────────────────────────────────────────────────────── */

  const filteredSessions = useMemo(() => {
    if (!searchQuery.trim()) return sessions;
    const q = searchQuery.toLowerCase();
    return sessions.filter(
      (s) =>
        s.id.toLowerCase().includes(q) ||
        s.channel.toLowerCase().includes(q) ||
        sessionDisplayTitle(s).toLowerCase().includes(q),
    );
  }, [sessions, searchQuery]);

  // companion_id → nome (cards L3, §5.2.1) — derivado da lista cacheada.
  const companionNameById = useMemo(
    () => new Map(companions.map((c) => [c.id, c.name])),
    [companions],
  );

  const chatActive = selected !== null || draftOpen;
  // §5.2.6 — runtime REAL (session.runtime) com heurística como fallback;
  // rascunho de conversa nova é sempre RAIOS/dashboard.
  const runtime = selected
    ? deriveSessionRuntime(selected)
    : draftOpen
      ? { key: 'raios', label: 'RAIOS', external: false }
      : null;
  const channelLabel = selected
    ? (CHANNEL_LABELS[selected.channel] ?? selected.channel)
    : 'Dashboard';
  // GATING POR CANAL (Breno 11/06): cada sessão só aceita interação pelo canal
  // em que NASCEU. Pela dashboard, só sessões channel='dashboard' são
  // interativas — evita o roteamento se perder (ex.: slash numa sessão de
  // telegram tentando resolver bot token). Runtime externo continua coberto
  // por implicação (sessão de runtime externo nunca nasce no canal dashboard).
  const readOnly = selected ? selected.channel !== 'dashboard' : false;
  const summary = selected ? sessionSummary(selected) : null;

  const sessionCompanion = useMemo(() => {
    if (companions.length === 0) return null;
    const byId = selected?.companion_id
      ? companions.find((c) => c.id === selected.companion_id)
      : null;
    if (byId) return byId;
    if (pendingCompanionId) {
      const pending = companions.find((c) => c.id === pendingCompanionId);
      if (pending) return pending;
    }
    // Fallback: único companion ativo (padrão CompanionPage — NUNCA is_default).
    const active = companions.filter((c) => c.is_active);
    return active.length === 1 ? active[0] : null;
  }, [companions, selected, pendingCompanionId]);

  const composerCompanionId = selected?.companion_id ?? sessionCompanion?.id ?? null;

  /* ─── Ações ─────────────────────────────────────────────────────────────── */

  const openSession = useCallback(async (session: Session) => {
    setSelected(session);
    setDraftOpen(false);
    setPendingModelOverride(null);
    setMessages(toChatMessages(session.messages));
    setAttachments([]);
    setDetailLoading(true);
    try {
      const full = await api.getSession(session.id);
      setSelected(full);
      setMessages(toChatMessages(full.messages));
    } catch {
      // Mantém os dados parciais da listagem
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const handleRename = useCallback(
    async (session: Session, title: string) => {
      try {
        const updated = await api.renameSession(session.id, title);
        const next = { ...session, ...updated, title: updated?.title ?? title };
        setSessions((prev) => prev.map((s) => (s.id === session.id ? { ...s, title: next.title } : s)));
        setSelected((prev) => (prev?.id === session.id ? { ...prev, title: next.title } : prev));
        toast({ message: 'Título atualizado.', type: 'success' });
      } catch (err) {
        // Backend antigo (sem PATCH /v1/sessions/:id) → degrada com toast de erro.
        toast({
          message:
            err instanceof Error && err.message
              ? `Não foi possível renomear: ${err.message}`
              : 'Não foi possível renomear a sessão.',
          type: 'error',
        });
      }
    },
    [toast],
  );

  const handleCopyId = useCallback(
    async (id: string) => {
      const ok = await copySessionId(id);
      toast({
        message: ok ? 'ID copiado.' : 'Não foi possível copiar o ID.',
        type: ok ? 'success' : 'error',
      });
    },
    [toast],
  );

  // §5.2.7 — nova conversa NÃO pré-cria a sessão: guarda companion + override
  // de modelo; a 1ª mensagem (session_id undefined) cria a sessão já com o
  // modelo escolhido.
  const startNewConversation = useCallback((selection: NewConversationSelection) => {
    setPendingCompanionId(selection.companionId);
    setPendingModelOverride(
      selection.providerKey || selection.modelKey
        ? {
            ...(selection.providerKey ? { provider_key: selection.providerKey } : {}),
            ...(selection.modelKey ? { model_key: selection.modelKey } : {}),
          }
        : null,
    );
    setSelected(null);
    setMessages([]);
    setAttachments([]);
    setDraftOpen(true);
    setNewConvOpen(false);
    inputRef.current?.focus();
  }, []);

  const handleAttachClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileSelected = useCallback(
    async (file: File | undefined) => {
      if (!file) return;
      setUploading(true);
      try {
        const uploaded = await api.uploadFile(file);
        setAttachments((prev) => [...prev, uploaded]);
        toast({ message: `Anexo "${uploaded.filename}" pronto para envio.`, type: 'success' });
      } catch (err) {
        toast({
          message: err instanceof Error ? err.message : 'Falha ao subir o arquivo.',
          type: 'error',
        });
      } finally {
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    },
    [toast],
  );

  // Núcleo do envio — usado pelo composer e pelas ações rápidas (§5.2.5).
  const sendContent = useCallback(
    async (content: string) => {
      if (!content || sending || readOnly) return;
      const companionId = composerCompanionId;
      if (!companionId) {
        toast({ message: 'Nenhum companion resolvido para esta sessão.', type: 'error' });
        return;
      }

      const userMsg: ChatMessage = {
        role: 'user',
        content,
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, userMsg]);
      setSending(true);

      try {
        const sessionId = selected?.id;
        // §5.2.7 — override só na criação (1ª mensagem sem session_id).
        const override = !sessionId && pendingModelOverride ? pendingModelOverride : undefined;
        const res = await api.sendMessage(content, 'dashboard', sessionId, companionId, override);
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: res.output, timestamp: new Date().toISOString() },
        ]);
        if (!sessionId || sessionId !== res.session_id) {
          // Sessão recém-criada pelo backend → adota como selecionada.
          setDraftOpen(false);
          setPendingModelOverride(null);
          try {
            const full = await api.getSession(res.session_id);
            setSelected(full);
            // Sessão recém-criada entra na lista sem refetch (o realtime
            // também cobre via INSERT — aqui é o fallback determinístico).
            setSessions((prev) =>
              prev.some((sess) => sess.id === full.id) ? prev : [full, ...prev],
            );
          } catch {
            setSelected(
              (prev) =>
                prev
                  ? { ...prev, id: res.session_id }
                  : ({
                      id: res.session_id,
                      tenant_id: '',
                      channel: 'dashboard',
                      status: 'active',
                      started_at: userMsg.timestamp,
                      ended_at: null,
                      messages: [],
                      companion_id: companionId,
                    } as Session),
            );
          }
        }
        // Lista atualiza via patch do realtime — ZERO refetch aqui (Breno
        // 11/06: a listagem ficava refazendo fetch o tempo todo durante a
        // conversa). A contagem de msgs da sessão aberta é patchada local:
        setSessions((prev) =>
          prev.map((sess) =>
            sess.id === res.session_id
              ? { ...sess, message_count: (sess.message_count ?? 0) + 2 }
              : sess,
          ),
        );
      } catch (err) {
        setMessages((prev) => [
          ...prev,
          {
            role: 'error',
            content: err instanceof Error ? err.message : 'Erro desconhecido',
            timestamp: new Date().toISOString(),
          },
        ]);
      } finally {
        setSending(false);
        inputRef.current?.focus();
      }
    },
    [sending, readOnly, composerCompanionId, selected, pendingModelOverride, toast],
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const text = input.trim();
      if (!text && attachments.length === 0) return;
      // Anexos viram links markdown no corpo da mensagem (signed URL do /v1/uploads).
      const attachmentBlock = attachments
        .map((a) => `[${a.filename}](${a.signed_url})`)
        .join('\n');
      const content = [attachmentBlock, text].filter(Boolean).join('\n\n');
      setInput('');
      setAttachments([]);
      await sendContent(content);
    },
    [input, attachments, sendContent],
  );

  // §5.2.5 — gating das ações rápidas: sessão read-only ou nenhuma → disabled + tooltip.
  const quickActionsEnabled = chatActive && !readOnly && !sending && !!composerCompanionId;
  const quickActionTitle = !chatActive
    ? 'Selecione uma sessão para usar as ações rápidas'
    : readOnly
      ? 'Sessão somente leitura — interaja pelo canal de origem'
      : !composerCompanionId
        ? 'Nenhum companion resolvido para esta sessão'
        : undefined;

  /* ─── Render ────────────────────────────────────────────────────────────── */

  return (
    <div className="space-y-4">
      <PageHeader title="Sessões" description="Conversas com o companion" />

      <div className="wf-sess h-[calc(100vh-10rem)]">
        {/* ═══ ESQUERDA: Nova conversa + busca + listagem (wireframe .sess-list) ═══
            MOBILE (correção 2026-07-19): abaixo de lg a lista era `hidden` — as
            sessões SUMIAM no celular/PWA e sobrava só o chat vazio. Agora é
            navegação empilhada: a LISTA é a tela quando não há conversa ativa;
            abrir uma sessão mostra o chat (com botão voltar no header). */}
        <div
          className={
            chatActive
              ? 'hidden lg:block self-stretch shrink-0'
              : 'block w-full lg:w-auto self-stretch shrink-0'
          }
        >
          <aside className="wf-sess-list w-full lg:w-[262px] h-full min-h-0">
            <button type="button" className="wf-newconv" onClick={() => setNewConvOpen(true)}>
              <Plus aria-hidden="true" />
              Nova conversa
            </button>

            <div className="wf-search">
              <Search aria-hidden="true" />
              <input
                placeholder="Buscar sessões"
                aria-label="Buscar sessões"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <div className="wf-slist wf-slist--fill">
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-24 rounded-[4px] shrink-0" />
                ))
              ) : error ? (
                <div className="wf-scard2 text-center" style={{ cursor: 'default' }}>
                  <p className="text-raios-danger-text text-xs">{error}</p>
                  <button type="button" className="wf-btn mt-3" onClick={() => void loadSessions()}>
                    Tentar novamente
                  </button>
                </div>
              ) : filteredSessions.length === 0 ? (
                <EmptyState
                  icon={MessageSquare}
                  title="Nenhuma sessão encontrada"
                  description="Inicie uma nova conversa para criar sua primeira sessão."
                  className="py-8"
                />
              ) : (
                filteredSessions.map((session) => (
                  <SessionCard
                    key={session.id}
                    session={session}
                    companionName={
                      session.companion_id
                        ? (companionNameById.get(session.companion_id) ?? null)
                        : null
                    }
                    isSelected={selected?.id === session.id}
                    onClick={() => void openSession(session)}
                    onRename={(title) => handleRename(session, title)}
                    onCopied={(ok) =>
                      toast({
                        message: ok ? 'ID copiado.' : 'Não foi possível copiar o ID.',
                        type: ok ? 'success' : 'error',
                      })
                    }
                  />
                ))
              )}
            </div>
          </aside>
        </div>

        {/* ═══ CENTRO: chat (wireframe .sess-chat) ═══
            MOBILE: sem conversa ativa, o chat some (a lista é a tela). */}
        <section
          className={
            chatActive
              ? 'wf-sess-chat flex-1 min-w-0 self-stretch'
              : 'wf-sess-chat flex-1 min-w-0 self-stretch hidden lg:flex'
          }
        >
          {!chatActive ? (
            <div className="flex-1 flex items-center justify-center">
              <EmptyState
                icon={MessageCircle}
                title="Selecione uma sessão"
                description="Escolha uma conversa na lista ou inicie uma nova."
              />
            </div>
          ) : (
            <>
              {/* Header (wireframe .chat-h): título editável + ID à esquerda;
                  runtime + canal + origem à direita */}
              <div className="wf-chat-h shrink-0">
                {/* MOBILE: voltar pra lista de sessões (navegação empilhada). */}
                <button
                  type="button"
                  className="lg:hidden shrink-0 p-2 -ml-1 mr-1 rounded-lg text-raios-text-secondary hover:bg-raios-elevated hover:text-raios-text transition-colors cursor-pointer"
                  onClick={() => {
                    setSelected(null);
                    setDraftOpen(false);
                    setMessages([]);
                  }}
                  aria-label="Voltar pra lista de sessões"
                  title="Voltar"
                >
                  <ChevronLeft className="w-5 h-5" aria-hidden="true" />
                </button>
                <div className="wf-ch-left">
                  {selected ? (
                    <>
                      <div className="wf-ch-title-row">
                        <EditableTitle
                          value={sessionDisplayTitle(selected)}
                          onSave={(title) => handleRename(selected, title)}
                          className="flex-1 min-w-0"
                          textClassName="wf-ch-title"
                        />
                      </div>
                      <div className="wf-ch-id">
                        <button
                          type="button"
                          className="wf-copybtn"
                          onClick={() => void handleCopyId(selected.id)}
                          aria-label="Copiar ID"
                          title="Copiar ID"
                        >
                          <Copy />
                        </button>
                        {shortSessionId(selected.id)}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="wf-ch-title-row">
                        <span className="wf-ch-title">Nova conversa</span>
                      </div>
                      <div className="wf-ch-id">—</div>
                    </>
                  )}
                </div>

                <div className="wf-ch-right">
                  {runtime && (
                    <span className={cn('wf-rtb', runtime.external ? 'openclaw' : 'raios')}>
                      {runtime.external ? <Lock aria-hidden="true" /> : <Zap aria-hidden="true" />}
                      {runtime.label}
                    </span>
                  )}
                  {/* Breno 11/06: SÓ runtime + canal de origem (a 3ª badge
                      "dashboard" não existe no wireframe e saiu) */}
                  <span className="wf-chb">
                    <Radio aria-hidden="true" />
                    {channelLabel}
                  </span>
                </div>
              </div>

              {/* Mensagens (wireframe .chat-body/.bub) */}
              <div className="wf-chat-body wf-chat-body--fill">
                {detailLoading && messages.length === 0 ? (
                  Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className="h-16 rounded-lg shrink-0" />
                  ))
                ) : messages.length === 0 ? (
                  <EmptyState
                    icon={MessageCircle}
                    title="Nenhuma mensagem"
                    description={
                      readOnly
                        ? 'Esta sessão não possui mensagens registradas.'
                        : 'Envie uma mensagem para começar a conversa.'
                    }
                    className="py-12"
                  />
                ) : (
                  messages.map((msg, i) => (
                    <div
                      key={i}
                      className={cn(
                        'wf-bub',
                        msg.role === 'user' ? 'u' : msg.role === 'error' ? 'err' : 'a',
                      )}
                    >
                      {msg.role === 'assistant' ? (
                        <MarkdownViewer content={msg.content} className="text-sm" />
                      ) : (
                        <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                      )}
                    </div>
                  ))
                )}

                {sending && (
                  <div className="wf-bub a">
                    <div className="flex gap-1.5 py-0.5">
                      <div className="w-2 h-2 rounded-full bg-raios-muted animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-2 h-2 rounded-full bg-raios-muted animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-2 h-2 rounded-full bg-raios-muted animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                )}
                <div ref={bottomRef} />
              </div>

              {/* Composer (wireframe .composer): clipe funcional + mic disabled */}
              <form onSubmit={(e) => void handleSubmit(e)} className="relative shrink-0">
                {/* §5.2.3 — slash autocomplete compartilhado */}
                <SlashAutocomplete
                  input={input}
                  inputRef={inputRef}
                  onPick={(value) => {
                    setInput(value);
                    inputRef.current?.focus();
                  }}
                />

                {/* Anexos pendentes */}
                {attachments.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 px-3 pt-2 border-t border-[var(--wf-border-muted)]">
                    {attachments.map((a) => (
                      <span
                        key={a.upload_id}
                        className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-raios-elevated/60 text-raios-text-secondary"
                      >
                        <Paperclip className="w-2.5 h-2.5" />
                        {a.filename}
                        <button
                          type="button"
                          aria-label={`Remover anexo ${a.filename}`}
                          onClick={() =>
                            setAttachments((prev) => prev.filter((x) => x.upload_id !== a.upload_id))
                          }
                          className="cursor-pointer hover:text-raios-text"
                        >
                          <X className="w-2.5 h-2.5" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                <div className="wf-composer">
                  {/* Clipe — upload canônico /v1/uploads (qualquer formato) */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    aria-label="Selecionar arquivo para anexar"
                    onChange={(e) => void handleFileSelected(e.target.files?.[0])}
                  />
                  <button
                    type="button"
                    className="wf-cbtn clip"
                    disabled={readOnly || uploading || sending}
                    onClick={handleAttachClick}
                    aria-label="Anexar arquivo"
                    title="Anexar arquivo ou imagem"
                  >
                    {uploading ? <Loader2 className="animate-spin" /> : <Paperclip />}
                  </button>

                  {/* Áudio — presente mas desabilitado (mockup .cbtn.mic.off) */}
                  <button
                    type="button"
                    className="wf-cbtn mic off"
                    disabled
                    aria-label="Áudio — em breve"
                    title="Áudio — em breve"
                  >
                    <Mic />
                  </button>

                  <input
                    ref={inputRef}
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder={
                      readOnly
                        ? `Somente leitura — sessão do canal ${channelLabel}`
                        : `Mensagem para ${sessionCompanion?.name ?? 'o companion'}…`
                    }
                    disabled={readOnly || sending}
                  />

                  <button
                    type="submit"
                    className="wf-cbtn send"
                    aria-label="Enviar"
                    title="Enviar"
                    disabled={
                      readOnly ||
                      sending ||
                      (!input.trim() && attachments.length === 0) ||
                      !composerCompanionId
                    }
                  >
                    <Send />
                  </button>
                </div>
              </form>

              {/* Lockbar — gating POR CANAL (wireframe .lockbar; Breno 11/06) */}
              {readOnly && (
                <div data-testid="session-lockbar" className="wf-lockbar shrink-0">
                  <Lock aria-hidden="true" />
                  <span>
                    Esta sessão foi iniciada no canal <strong>{channelLabel}</strong> — a
                    conversa continua por lá. Aqui ela fica somente leitura.
                  </span>
                </div>
              )}
            </>
          )}
        </section>

        {/* ═══ DIREITA: Companion · Ações rápidas · Sessão atual ═══
            Wireframe v18: .sess-side é o CONTAINER (border+surface) que envolve
            os 3 side-cards e preenche a altura da linha — alinhado no TOPO com
            lista e chat. Botão "Contexto" removido (não existe no wireframe;
            Breno 11/06). */}
        <div className="hidden lg:block self-stretch shrink-0">
          <aside className="wf-sess-side wf-sess-side--fill w-[246px]">
              {/* 1. Companion da sessão */}
              <div className="wf-side-card">
                <div className="wf-sc-h">Companion da sessão</div>
                {companionsLoading ? (
                  <Skeleton className="h-10 rounded-lg" />
                ) : sessionCompanion ? (
                  <div className="wf-comp">
                    {sessionCompanion.avatar_url ? (
                      <img className="av" src={sessionCompanion.avatar_url} alt="" />
                    ) : (
                      <div className="av" aria-hidden="true">
                        {sessionCompanion.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="cn truncate">{sessionCompanion.name}</div>
                      <div className="cr truncate">{sessionCompanion.slug}</div>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-raios-muted">Companion não identificado.</p>
                )}
              </div>

              {/* DEM-2550 — Control Plane (provider/modelo/effort/reasoning) */}
              {composerCompanionId && (
                <div className="wf-side-card !p-0 overflow-hidden">
                  <ControlPlanePanel
                    companionId={composerCompanionId}
                    sessionId={selected?.id ?? null}
                    compact
                  />
                </div>
              )}

              {/* 2. Ações rápidas (§5.2.5 — enviam mensagem na sessão ativa) */}
              <div className="wf-side-card">
                <div className="wf-sc-h">Ações rápidas</div>
                {QUICK_ACTIONS.map((action) => (
                  <button
                    key={action.key}
                    type="button"
                    className="wf-qabtn"
                    disabled={!quickActionsEnabled}
                    title={quickActionTitle ?? action.label}
                    onClick={() => {
                      setInput('');
                      void sendContent(action.message);
                    }}
                  >
                    <action.icon aria-hidden="true" />
                    {action.label}
                  </button>
                ))}
              </div>

              {/* 3. Sessão atual — ajusta ao CONTEÚDO (não estica); o resumo
                  expande até o limite do container/viewport e trunca com fade */}
              <div className="wf-side-card wf-side-card--resumo">
                <div className="wf-sc-h">Sessão atual</div>
                {chatActive ? (
                  <>
                    <dl className="wf-kv">
                      <div className="wf-kvr">
                        <span className="wf-kk">
                          <Zap aria-hidden="true" />
                          Runtime
                        </span>
                        <span className="wf-vv">{runtime?.label}</span>
                      </div>
                      <div className="wf-kvr">
                        <span className="wf-kk">
                          <Radio aria-hidden="true" />
                          Canal
                        </span>
                        <span className="wf-vv">{channelLabel}</span>
                      </div>
                      <div className="wf-kvr">
                        <span className="wf-kk">
                          <Clock aria-hidden="true" />
                          Início
                        </span>
                        <span className="wf-vv">
                          {selected ? fmtDateTime(selected.started_at) : '—'}
                        </span>
                      </div>
                      <div className="wf-kvr">
                        <span className="wf-kk">
                          <MessageCircle aria-hidden="true" />
                          Mensagens
                        </span>
                        <span className="wf-vv">
                          {messages.length || (selected?.message_count ?? 0)}
                        </span>
                      </div>
                    </dl>

                    <div className="wf-sresumo wf-sresumo--dyn">
                      <strong>Resumo:</strong>{' '}
                      {summary ?? 'Sem resumo disponível para esta sessão.'}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-6">
                    <MessageCircle
                      className="w-8 h-8 text-raios-muted mx-auto mb-2"
                      strokeWidth={1.5}
                    />
                    <p className="text-xs text-raios-muted">Nenhuma sessão ativa</p>
                  </div>
                )}
              </div>
          </aside>
        </div>
      </div>

      {/* Modal Nova conversa (SESS-08) */}
      <NewConversationModal
        open={newConvOpen}
        onClose={() => setNewConvOpen(false)}
        onConfirm={startNewConversation}
        companions={companions}
        companionsLoading={companionsLoading}
      />
    </div>
  );
}
